// apps/office/src/app/analytics/pendo/pendo-ol.ts
import { track } from '../../../../../libs/analytics/pendo';

/** tiny throttle */
export function throttle<T extends (...a: any[]) => void>(fn: T, ms: number): T {
  let last = 0, t: any;
  return function (this: any, ...args: any[]) {
    const now = Date.now(), invoke = () => { last = now; t = null; fn.apply(this, args); };
    if (now - last >= ms) invoke(); else { clearTimeout(t); t = setTimeout(invoke, ms - (now - last)); }
  } as any;
}

const baseCtx = () => ({
  env: location.host.includes('staging') ? 'staging' : 'prod',
  path: location.pathname + location.hash,
});

/** Wire a single OpenLayers Map instance to Pendo */
export function wirePendoToOpenLayers(map: any /* ol/Map */) {
  // 1) prevent double wiring
  if ((map as any).__pendoWired) return;
  (map as any).__pendoWired = true;

  // 2) first render (loaded)
  const view = map.getView();
  const fireLoaded = () => {
    try {
      const c = view.getCenter() || [0, 0];
      track('Map:Loaded', {
        ...baseCtx(),
        provider: 'openlayers',
        zoom: view.getZoom(),
        centerX: Number((c[0] ?? 0).toFixed?.(2) ?? 0),
        centerY: Number((c[1] ?? 0).toFixed?.(2) ?? 0),
        projection: view.getProjection?.()?.getCode?.() || null,
      });
    } catch { /* noop */ }
  };

  // `rendercomplete` = clearer “ready” moment; fallback to postrender
  if (map.on) {
    map.once?.('rendercomplete', fireLoaded) ?? map.once?.('postrender', fireLoaded);
  } else {
    // if some wrapper calls this before map events are ready, fire later
    setTimeout(fireLoaded, 0);
  }

  // 3) zoom/pan (throttled)
  const onViewChanged = throttle(() => {
    try {
      const c = view.getCenter() || [0, 0];
      const extent = view.calculateExtent(map.getSize());
      track('Map:ViewChanged', {
        ...baseCtx(),
        zoom: view.getZoom(),
        centerX: Number((c[0] ?? 0).toFixed?.(2) ?? 0),
        centerY: Number((c[1] ?? 0).toFixed?.(2) ?? 0),
        extent, // [minX,minY,maxX,maxY] in map projection
      });
    } catch { /* noop */ }
  }, 1000);

  view.on?.('change:center', onViewChanged);
  view.on?.('change:resolution', onViewChanged);

  // 4) feature selected
  map.on?.('singleclick', (evt: any) => {
    let recorded = false;
    map.forEachFeatureAtPixel?.(
      evt.pixel,
      (feature: any, layer: any) => {
        if (recorded) return;
        track('Map:FeatureSelected', {
          ...baseCtx(),
          layerId: layer?.get?.('name') ?? layer?.get?.('id') ?? null,
          featureId: feature?.getId?.() ?? feature?.id ?? feature?.get?.('id') ?? null,
          geomType: feature?.getGeometry?.()?.getType?.(),
        });
        recorded = true;
      },
      { hitTolerance: 4 } // makes selects on thin lines easier
    );
  });
}

/** Call when your UI shows/hides a layer */
export function pendoLayerToggled(layer: any /* ol/layer/Base */, visible: boolean) {
  const name = layer?.get?.('name') ?? layer?.get?.('id') ?? 'unknown';
  track('Map:LayerToggled', { ...baseCtx(), layerId: name, visible: !!visible });
}
